import cx from "classnames";
import { faPrescription } from "@fortawesome/pro-regular-svg-icons";

import { toDollars } from "common/utils/formatting";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { urls } from "client/routes";
import { ProductListSource } from "client/analytics/types";

import { ProductSearchResult } from "../types";
import { useProductCardFields } from "../hooks/utils";
import { canInlineBadgesOnMobileCard } from "../utils";
import ProductSearchAutoshipPricing from "./ProductSearchAutoshipPricing";
import ProductCardBadges from "./ProductCardBadges";

import styles from "../styles/ProductCardMobile.module.less";

type ProductCardMobileProps = {
  result: ProductSearchResult;
  listSource: ProductListSource;
  collectionSlug?: string;
  categoryId?: number;
  /** Runs after the click is reported, e.g. to close the drawer the card sits in. */
  onNavigate?: () => void;
};

/** The mobile product card: the image beside the product's details and prices. */
const ProductCardMobile = ({
  result,
  listSource,
  collectionSlug,
  categoryId,
  onNavigate,
}: ProductCardMobileProps) => {
  const {
    productLine,
    hasPrescriptionLabel,
    isFeatured,
    variantLabel,
    packLabel,
    optionsLabel,
    facts,
    handleClick,
  } = useProductCardFields(result, listSource, { collectionSlug, categoryId, onNavigate });

  const defaultItem = result.default_item;
  const hasStruckPrice = defaultItem.standard_list_price > defaultItem.standard_price;
  const showBadgesInline = canInlineBadgesOnMobileCard(defaultItem.badges);
  // The badge row is styles-injected, so the stacked placement swaps its container class.
  const stackedBadgeStyles = { ...styles, badgeRow: styles.badgeRowStacked };
  const featuredLabel = isFeatured ? "Your Vet's Choice" : null;
  const cornerLabel = hasPrescriptionLabel ? result.patient_prescription_label : featuredLabel;
  const dest = urls.products.detail({
    params: { id: productLine.id },
    search: { item: defaultItem.id, product: result.id },
  });

  return (
    <div className={styles.card}>
      {!!cornerLabel && (
        <>
          <div
            className={cx(
              styles.bannerRing,
              hasPrescriptionLabel ? styles.prescribed : styles.featured,
            )}
          />
          <TextChip
            size="s"
            variant={hasPrescriptionLabel ? "info" : "highlight"}
            className={styles.banner}
          >
            {cornerLabel}
          </TextChip>
        </>
      )}
      <div className={cx(styles.media, !!cornerLabel && styles.mediaBelowBanner)}>
        <CatalogImage
          image={result.main_image}
          size="medium"
          alt={result.name}
          className={styles.image}
          imageClassName={styles.imageInner}
        />
      </div>
      <LinkButton
        dest={dest}
        className={cx(styles.link, !!cornerLabel && styles.linkBelowBanner)}
        onClick={handleClick}
      >
        {productLine.prescription_required && (
          <Icon
            icon={faPrescription}
            className={styles.prescription}
            title="Prescription required"
          />
        )}
        <p className={styles.name}>{productLine.name}</p>
        {variantLabel && (
          <div className={styles.variant}>
            <span>{variantLabel}</span>
          </div>
        )}
        {(packLabel || optionsLabel) && (
          <div className={styles.breakdown}>
            {packLabel && <span className={styles.pack}>{packLabel}</span>}
            {optionsLabel && <span className={styles.options}>+{optionsLabel}</span>}
          </div>
        )}
        <div className={styles.priceRow}>
          {hasStruckPrice && (
            <span className={styles.priceWas}>{toDollars(defaultItem.standard_list_price)}</span>
          )}
          <span className={styles.price}>{toDollars(defaultItem.standard_price)}</span>
          {showBadgesInline && <ProductCardBadges badges={defaultItem.badges} styles={styles} />}
        </div>
        <ProductSearchAutoshipPricing defaultItem={defaultItem} styles={styles} />
        {!showBadgesInline && (
          <ProductCardBadges badges={defaultItem.badges} styles={stackedBadgeStyles} />
        )}
      </LinkButton>
      {facts.length > 0 && (
        <div className={styles.facts}>
          {facts.map((fact) => (
            <TextChip key={`${fact.label}-${fact.value}`} variant="grey" size="m">
              {fact.value}
            </TextChip>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCardMobile;
